import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import type { AuthContextValue, AuthState, RegisterRequest } from '../types/auth';
import * as authService from '../services/auth';
import { UserRole } from '../types/user';

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
    error: null,
  });

  /**
   * Initialize auth state from stored tokens
   */
  useEffect(() => {
    const initAuth = async () => {
      const token = authService.getAccessToken();
      const refreshTokenValue = authService.getRefreshToken();

      if (!token && !refreshTokenValue) {
        setState((prev) => ({ ...prev, isLoading: false }));
        return;
      }

      if (refreshTokenValue && (!token || authService.isTokenExpired())) {
        try {
          const refreshResponse = await authService.refreshToken(token || '', refreshTokenValue);
          const rememberMe = authService.wasRememberMe();
          authService.storeTokens(refreshResponse.token, refreshResponse.refreshToken, refreshResponse.expiresAt, rememberMe);

          const user = await authService.getMe();
          setState({
            user,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });
          return;
        } catch (error) {
          authService.clearTokens();
          setState({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
          });
          return;
        }
      }

      if (token) {
        try {
          const user = await authService.getMe();
          setState({
            user,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });
        } catch (error) {
          if (refreshTokenValue) {
            try {
              const refreshResponse = await authService.refreshToken(token, refreshTokenValue);
              const rememberMe = authService.wasRememberMe();
              authService.storeTokens(refreshResponse.token, refreshResponse.refreshToken, refreshResponse.expiresAt, rememberMe);

              const user = await authService.getMe();
              setState({
                user,
                isAuthenticated: true,
                isLoading: false,
                error: null,
              });
              return;
            } catch {
              // Token refresh failed
            }
          }

          authService.clearTokens();
          setState({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
          });
        }
      }
    };

    initAuth();
  }, []);

  const login = useCallback(async (email: string, password: string, rememberMe = false) => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      const response = await authService.login(email, password, rememberMe);

      authService.storeTokens(response.token, response.refreshToken, response.expiresAt, rememberMe);

      setState({
        user: response.user,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      });
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || 'Login failed';
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }));
      throw error;
    }
  }, []);

  const register = useCallback(async (data: RegisterRequest) => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      await authService.register(data);

      // After successful registration, user needs to login
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: null,
      }));
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || 'Registration failed';
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }));
      throw error;
    }
  }, []);

  const logout = useCallback(async () => {
    const refreshToken = authService.getRefreshToken();

    try {
      await authService.logout(refreshToken || undefined);
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear tokens and state regardless of API call success
      authService.clearTokens();
      setState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      });
    }
  }, []);

  const refreshAuth = useCallback(async () => {
    try {
      const user = await authService.getMe();
      setState((prev) => ({
        ...prev,
        user,
        isAuthenticated: true,
      }));
    } catch (error) {
      console.error('Failed to refresh auth:', error);
      authService.clearTokens();
      setState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      });
    }
  }, []);

  const value: AuthContextValue = {
    ...state,
    login,
    register,
    logout,
    refreshAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function useHasRole(role: string): boolean {
  const { user } = useAuth();
  return user?.roles?.includes(role) ?? false;
}

export function useIsAdmin(): boolean {
  const { user } = useAuth();
  return user?.roles?.some((r: string) => [UserRole.Admin, UserRole.SuperAdmin, UserRole.Owner].includes(r as UserRole)) ?? false;
}

export function useIsSuperAdmin(): boolean {
  const { user } = useAuth();
  return (user?.roles?.includes(UserRole.SuperAdmin) || user?.roles?.includes(UserRole.Owner)) ?? false;
}

